import React, { FunctionComponent } from 'react';
import { Button } from '@companion-professional/components';
import { CheckupTag } from '@companion-professional/dctypes';
import { cn, snakeToSentenceCase } from '@companion-professional/webutils';

interface CheckupLocationSelectionProps {
  // onTagSelect is a function called when a location tag is selected.
  onTagSelect: (tag: CheckupTag) => Promise<void>;

  // locationTags are the location tags that the user can select from to create a checkup.  Only one location tag can be
  // selected and the handleCreateCheckup function will be called with the selected location tag.
  locationTags: CheckupTag[];

  // buttonType is the type of button that is rendered for the location tags.  The default is 'button', but if you want
  // to use a submit button, you can set this to 'submit'.
  buttonType?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];

  // selectedCheckupTags are the tags that are associated with the checkup.  If the tag is in the selectedCheckupTags
  // array, then the button will be styled as selected.
  selectedCheckupTags?: CheckupTag[];
}

// CheckupLocationSelectionButtons is a component that displays a list of location tag buttons that the user can select
// from. When a location tag is selected, the onTagSelect function is called with the selected location tag.
export const CheckupLocationSelectionButtons: FunctionComponent<CheckupLocationSelectionProps> = ({
  onTagSelect,
  locationTags,
  selectedCheckupTags,
  buttonType = 'button'
}) => {
  return (
    <div className="flex w-full flex-row gap-2">
      {locationTags.map((tag) => (
        <Button
          key={tag}
          variant={selectedCheckupTags?.includes(tag) ? 'primary' : 'outline'}
          size="small"
          onClick={() => {
            // Don't do anything if the tag is already selected.
            if (selectedCheckupTags?.includes(tag)) {
              return;
            }
            return onTagSelect(tag);
          }}
          type={buttonType}
          className={cn('max-w-40 flex-1', {
            // If the tag has been selected, override the brightness and the cursor so that it doesn't seem to be
            // interactive.
            'cursor-default hover:brightness-100': selectedCheckupTags?.includes(tag)
          })}
        >
          {snakeToSentenceCase(tag)}
        </Button>
      ))}
    </div>
  );
};
